
export class MonoprixRecipeInjector {
  storeWrapper;
  productsAndCardPositions = [];

  constructor(storeWrapper) {
    this.storeWrapper = storeWrapper;
    this.initCardInjection();
  }

  initCardInjection() {
    this.productsAndCardPositions = this.cardsPositionsByScreenWidth();
    this.injectCards();
    const pageListenerInject = new MutationObserver(() => this.injectCards())
    pageListenerInject.observe(document.body, { attributes: false, childList: true, subtree: true });
    const pageListenerRemove = new MutationObserver(() => this.removeCardsIfNecessary())
    pageListenerRemove.observe(document.body, { attributes: false, childList: true, subtree: true });
  }

  /* ******************************************************* CARD POSITIONS ******************************************************** */

  /**
   * @returns An array of couples of the *final* positions of the cards and the *initial* position of their preceding product
   */
  cardsPositionsByScreenWidth() {
    let productsPosition;
    const tilesContainerWidth = document.body.offsetWidth;
    if (tilesContainerWidth < 615) {
      // 2 tiles by row
      productsPosition = [5, 11, 14, 21];
    } else if (tilesContainerWidth < 800) {
      // 3 tiles by row
      productsPosition = [7, 10, 13, 19];
    } else if (tilesContainerWidth < 1200) {
      // 4 tiles by row
      productsPosition = [7, 12, 16, 21];
    } else if (tilesContainerWidth < 1455) {
      // 5 tiles by row
      productsPosition = [7, 9, 16, 18];
    } else if (tilesContainerWidth < 1696) {
      // 6 tiles by row
      productsPosition = [7, 15, 16, 28];
    } else if (tilesContainerWidth < 1936) {
      // 7 tiles by row
      productsPosition = [7, 16, 20, 23];
    } else {
      // 8 or more tiles by row
      productsPosition =   [7, 13, 18, 24];
    }

    const offset = (document.querySelector('[data-test="product-list-ad-container"]')?.children.length) ? -2 : 0; 

    // cards should be injected after products at initial position p, which makes them be in the final position p + i + 1;
    return productsPosition.map((p, i) => ({ positionOfPrecedingProduct: p + offset, cardPosition: p + i + 1 + offset }))
  }

  /* ******************************************************* CARD INJECTION AND REMOVAL ******************************************************** */

  removeCardsIfNecessary() {
    if (!this.storeWrapper.isAvailable() || this.areAllProductsLoading() || this.numberOfProductsPerLineChanged()) {
      document.querySelectorAll('.miamRecipeCardContainer').forEach(card => card.remove());
    }
  }

  /**
   * If we can find the product list, try to inject a card for each position
   */
  injectCards() {
    if (this.storeWrapper.isAvailable()) {
      const cardMainContainer = this.getCardMainContainer();
      if (cardMainContainer && cardMainContainer.children) {
        this.productsAndCardPositions.forEach(position => {
          this.injectCardIfNecessary(position)
        })
      }
    }
  }

  /**
   * If the element at position is not a recipe-card, try to add a card at the position or move the card that should be at position
   */
  injectCardIfNecessary(position) {
    const list = this.getCardMainContainer();
    const products = this.getProductsAndCardsInList(list.children);
    const productAtPosition = products[position.cardPosition];
    if (productAtPosition && (+(productAtPosition.dataset.intendedPosition) !== position.cardPosition)) {
      this.insertCardAfterProduct(position);
    }
  }

  /**
   * If the card for the position is on the page, move it at the position
   * Else, try to add a new card at position
   */
  insertCardAfterProduct(position) {
    const list = this.getCardMainContainer();
    const products = this.getProductsInList(list.children);
    const precedingProduct = products[position.positionOfPrecedingProduct];
    const followingProduct = products[position.positionOfPrecedingProduct + 1];
    if (precedingProduct && followingProduct) {
      // Try to get the card if already in the DOM
      let cardContainer = this.searchExistingCardForPosition(position);
      if (!cardContainer) {
        // Try to create card
        const productsIds = this.getProductsIds([precedingProduct, followingProduct]);
        if(productsIds.length !== 0 && productsIds[0] !== '') {
          cardContainer = this.createCard(productsIds);
          // Add a dataset value to keep track of where the card should be and compare with where it actually is
          cardContainer.dataset.intendedPosition = position.cardPosition; 
        }
      }
      if (cardContainer) {
        precedingProduct.after(cardContainer);
      }
    }
  }

  searchExistingCardForPosition(position) {
    return document.querySelector(`[data-intended-position="${position.cardPosition}"]`);
  }

  /* ******************************************************* CARD CONTAINER CREATION ******************************************************** */

  /**
   * Create the card and the container around it
   */
  createCard(productsIds) {
    const cardContainer = document.createElement('div');
    cardContainer.classList.add('miamRecipeCardContainer');

    const context = productsIds.map(p => { return { id: p }});
    console.debug('[Miam] Set context with items ids:', productsIds);
    const webcCard = document.createElement('webc-miam-recipe-card');
    webcCard.productIds = context;
    webcCard.displayVariant = 2;
    webcCard.displayInfos = true;
    webcCard.addEventListener('hide', () => {
      console.debug(`[Miam] card hidden because no recipe was found for ${context}`);
      cardContainer.style.display = 'none';
    });

    cardContainer.appendChild(webcCard);
    return cardContainer;
  }

  /* ******************************************************* UTILS ******************************************************** */

  /**
   * Find the product list
   */
  getCardMainContainer() {
    return document.querySelector('[data-synthetics="product-list"]') || document.querySelector('[data-retailer-anchor="product-list"]')
  }

  /**
   * @returns true if the screen size has passed a threshold that changes the number of items per line
   */
  numberOfProductsPerLineChanged() {
    const positionsForCurrentWidth = this.cardsPositionsByScreenWidth();
    const hasChanged = this.productsAndCardPositions?.some((p, i) => p.cardPosition !== positionsForCurrentWidth[i].cardPosition) ?? false;
    this.productsAndCardPositions = positionsForCurrentWidth;
    return hasChanged;
  }

  areAllProductsLoading() {
    const cardMainContainer = this.getCardMainContainer();
    if (!cardMainContainer) {
      return true;
    }
    return !(this.getProductsInList(cardMainContainer.children).some(x => x.dataset.test !== 'fop-skeleton'));
  }

  getProductsInList(list) {
    return Array.from(list)
    .filter(x =>
      x.dataset.test !== 'advertising-banner' &&
      x.dataset.test !== 'missed-promotion-alternative-text' &&
      x.dataset.test !== 'product-list-ad-container' &&
      !(x.classList?.contains('miamRecipeCardContainer'))
    );
  }

  getProductsAndCardsInList(list) {
    return Array.from(list)
    .filter(x =>
      x.dataset.test !== 'advertising-banner' &&
      x.dataset.test !== 'missed-promotion-alternative-text' &&
      x.dataset.test !== 'product-list-ad-container'
    );
  }

  getProductsIds(products) {
    return products.map(product => {
      const link = product.querySelector('[data-synthetics="bop-link"]');
      const result = link?.href.match(/(MPX_[0-9]+)/);
      return result && result[1] || '';
    });
  }
}
